<template>
  <div class="pilot-player">
    <pilot-menu
      v-show="showPilotMenuPage"
      :tour-id="tourId"
      :pilot-tours="pilotTours"
      :pilot-influencers="pilotInfluencers"
      @change-page="changePage"
      @select-tour="selectTour"
      @select-influencer="selectInfluencer"
      @show-player="showPlayer"
      @close="close"
      @toggle-pilot-player-status="togglePilotPlayerStatus"
    />
    <transition :name="transitionName">
      <pilot-influencer
        v-show="showPilotInfluencerPage"
        :tour-id="tourId"
        :pilot-influencer="pilotInfluencer"
        :pilot-tours="pilotTours"
        @change-page="changePage"
        @select-tour="selectTour"
        @show-player="showPlayer"
        @close="close"
        @toggle-pilot-player-status="togglePilotPlayerStatus"
      />
    </transition>
    <pilot-tour
      v-show="showPilotTourPage"
      ref="pilotTourRef"
      :pilot-tour="pilotTour"
      :is-large-screen="isLargeScreen"
      @change-page="changePage"
      @close="close"
    />
    <pilot-tooltip @confirm="showPlayer" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { findPilots as fetchPilotListFromFireBase } from '@/plugins/firebase'
import { userSVC } from '@/plugins/axios/services'
import PilotMenu from './PilotMenu'
import PilotInfluencer from './PilotInfluencer'
import PilotTour from './PilotTour'
import PilotTooltip from '../PilotTooltip'

export default {
  name: 'PilotPlayer',
  components: {
    PilotMenu,
    PilotInfluencer,
    PilotTour,
    PilotTooltip
  },
  data () {
    return {
      page: 'pilot-menu',
      pilotInfluencers: [],
      userId: null,
      influencerId: null,
      tourId: null,
      pilotTour: null,
      checkPermissionTimeout: null,
      transitionName: 'fade',
      windowWidth: window.innerWidth,
      isUserIdInitialized: false,
      isQueryPilotInitialized: false
    }
  },
  computed: {
    ...mapGetters([
      'currentPanorama',
      'pilotList',
      'isPilotListActive',
      'isPilotPlayerActive',
      'isPilotTooltipActive',
      'isPilotPlayerExpanded',
      'isPilotPlaying'
    ]),
    ...mapGetters('auth', [
      'sessionLoginLoading'
    ]),
    pilotInfluencer () {
      return this.pilotInfluencers.find(influencer => influencer.id === this.influencerId)
    },
    pilotTours () {
      if (this.showPilotInfluencerPage) {
        return this.pilotList.filter(pilot => pilot.ownerId === this.influencerId)
      } else {
        return this.pilotList
      }
    },
    showPilotMenuPage () {
      return this.isPilotListActive && this.page === 'pilot-menu'
    },
    showPilotInfluencerPage () {
      return this.isPilotListActive && this.page === 'pilot-influencer'
    },
    showPilotTourPage () {
      return this.isPilotPlayerActive
    },
    pilotTourRef () {
      return this.$refs.pilotTourRef
    },
    isLargeScreen () {
      return this.windowWidth >= 1024
    }
  },
  watch: {
    currentPanorama: {
      handler (value) {
        try {
          this.close('pilot-menu') // or this.close('pilot-influencer') they are the same here
          this.initialize(value.objectId)
        } catch (error) {}
      },
      immediate: true
    }
  },
  mounted () {
    window.addEventListener('resize', this.onResize)
    this.$root.$on('play-pilot', this.$refs.pilotTourRef.playPilot)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    clearTimeout(this.checkPermissionTimeout)
    this.$root.$off('play-pilot', this.$refs.pilotTourRef.playPilot)
  },
  methods: {
    ...mapActions([
      'setPilotList',
      'showPilotList',
      'closePilotList',
      'shrinkPilotPlayer',
      'showPilotPlayer',
      'closePilotPlayer'
    ]),
    changePage (page) {
      if (this.isLargeScreen) {
        this.transitionName = 'fade'
      } else {
        this.transitionName = 'slide'
      }
      this.page = page
      switch (page) {
        case 'pilot-menu':
          this.showPilotList()
          break
        case 'pilot-influencer':
          this.showPilotList()
          break
        default:
          break
      }
    },
    showPlayer () {
      this.closePilotList()
      this.shrinkPilotPlayer()
      this.showPilotPlayer()
      this.pilotTourRef.playPilot()
    },
    selectInfluencer (influencerId) {
      this.influencerId = influencerId
    },
    selectTour (tourId) {
      this.tourId = tourId
      this.pilotTour = this.pilotTours.find(item => item.objectId === this.tourId)
    },
    close (target) {
      this.transitionName = 'fade'
      switch (target) {
        case 'pilot-menu':
        case 'pilot-influencer':
          this.closePilotList()
          break
        case 'pilot-tour':
          this.closePilotPlayer()
          break
        default:
          break
      }
    },
    togglePilotPlayerStatus () {
      this.pilotTourRef.togglePilotPlayerStatus()
    },
    async fetchUserId () {
      if (!this.isUserIdInitialized) {
        return
      }

      if (this.sessionLoginLoading) {
        // Call API until session Login Loading is false
        this.checkPermissionTimeout = setTimeout(() => {
          this.fetchUserId()
        }, 300)
      }
      try {
        const response = await userSVC.getUserProfile()
        if (response.data) {
          this.userId = response.data.objectId
        }
        this.isUserIdInitialized = true
      } catch (error) {
        throw Error(`fetchUserId Error: ${error}`)
      }
    },
    async fetchPilotList ({ panoramaId, userId }) {
      try {
        const { pilots, influencers } = await fetchPilotListFromFireBase(panoramaId, userId)
        this.setPilotList(pilots)
        this.pilotInfluencers = influencers

        // 這邊分成兩種情況
        // 1. 第一次使用 query pilot 初始化
        // 2. 已經初始化過，但 pilotTour 為 null，代表初始化時沒有 pilotTour，下一次進入新 panorama 時，需要重新初始化
        if (!this.isQueryPilotInitialized || this.pilotTour == null) {
          this.isQueryPilotInitialized = true
          if (this.pilotTours.find(item => item.objectId === this.$route.query.pilot)) {
            this.selectTour(this.$route.query.pilot)
          } else if (this.pilotTours.find(item => item.objectId !== this.$route.query.pilot) && this.pilotTours.length > 0) {
            this.selectTour(this.pilotTours[0].objectId)
          }
        }
      } catch (error) {
        throw Error(`fetchPilotList Error: ${error}`)
      }
    },
    async initialize (panoramaId) {
      try {
        await this.fetchUserId()
        await this.fetchPilotList({
          panoramaId,
          userId: this.userId
        })
      } catch (error) {}
    },
    onResize () {
      this.windowWidth = window.innerWidth
    }
  }
}
</script>

<style lang="stylus" scoped>
.slide-enter-active, .slide-leave-active {
  transition: all .3s ease
}
.slide-enter, .slide-leave-to /* .slide-leave-active below version 2.1.8 */ {
  transform: translateX(100%)
}
</style>
